import { useState } from 'react';
import { ansichtSitzung, hinweisNach, hinweisZiel, merkeAnsichtSitzung, sitzungFuerNutzer, wirksameAnsicht, type Ansicht, type HinweisEreignis } from '../components/guide-oberthemen';
import { leseGuideAnsichtMerk, setGuideAnsichtMerk, useGuideAnsichtMerk } from './useUiPreferences';
import { oeffneErklaerkarte } from './guideErklaerkarteSteuerung';

/**
 * Ansicht und Hinweis der Hofansicht fuer EINEN Nutzer. Gemerkt wird ueber `useUiPreferences` (der vorgesehene Ort fuer Nutzer-Vorlieben, kein eigener Speicher):
 * (1) `hinweisGesehen` bei „Verstanden“, „Ziele ansehen“ und beim bewussten Oeffnen der Ziele-Ansicht; (2) die bewusst gewaehlte Ansicht. Je Nutzerkennung getrennt; ein
 * Nutzerwechsel ohne Neuladen leitet den Zustand neu aus dem ab, was der NEUE Nutzer gemerkt hat. Gemerkt werden nur diese zwei Werte, nie Inhalte.
 */
export function useGuideAnsichtSitzung(nutzer: string, istBetreiber: boolean) {
  const [gehalten, setGehalten] = useState(() => ansichtSitzung(nutzer, istBetreiber, leseGuideAnsichtMerk(nutzer)));
  /* Nutzerwechsel ohne Neumounten: Ansicht, Hinweis und Stern des Vorgaengers gelten nicht weiter. */
  const sitzung = sitzungFuerNutzer(gehalten, nutzer, istBetreiber, leseGuideAnsichtMerk(nutzer));
  if (sitzung !== gehalten) setGehalten(sitzung);
  const ansicht: Ansicht = wirksameAnsicht(sitzung.wahl, istBetreiber);
  const aendere = (neu: typeof sitzung) => { merkeAnsichtSitzung(neu); setGehalten(neu); };
  const setAnsicht = (a: Ansicht) => {
    setGuideAnsichtMerk(nutzer, a === 'oberthema' ? { wahl: a, hinweisGesehen: true } : { wahl: a });
    aendere({ ...sitzung, wahl: a, hinweis: a === 'oberthema' ? hinweisNach(sitzung.hinweis, 'ziele-geoeffnet') : sitzung.hinweis });
  };
  const hinweisEreignis = (e: HinweisEreignis) => {
    if (hinweisZiel(e, istBetreiber) === 'karte') {
      /* Partner: dieselbe Karte oeffnen (kein zweites Feld); der Stern ist damit gesehen. */
      if (e === 'stern') setGuideAnsichtMerk(nutzer, { hinweisGesehen: true });
      oeffneErklaerkarte();
      return;
    }
    if (e === 'verstanden') setGuideAnsichtMerk(nutzer, { hinweisGesehen: true });
    if (e === 'ziele-ansehen') setGuideAnsichtMerk(nutzer, { hinweisGesehen: true, wahl: 'oberthema' });
    if (e === 'ziele-ansehen') aendere({ ...sitzung, wahl: 'oberthema', hinweis: hinweisNach(sitzung.hinweis, e) });
    else aendere({ ...sitzung, hinweis: hinweisNach(sitzung.hinweis, e) });
  };
  /* Wurde der Hinweis anderswo gesehen (z. B. die Karte zugeklappt), gilt das hier sofort: der Stern verschwindet. `offen` bleibt, wie die Sitzung es hat. */
  const merk = useGuideAnsichtMerk(nutzer);
  const sichtbar = merk?.hinweisGesehen ? { ...sitzung, hinweis: { ...sitzung.hinweis, geschlossen: true, zieleGeoeffnet: true } } : sitzung;
  return { sitzung: sichtbar, ansicht, setAnsicht, hinweisEreignis };
}
